import { createFileRoute, Outlet, useMatches, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { RealEstateProjectFiltersProvider } from '~/components/routes/development/real-estate/real-estate-layout-context'
import type { StatusFilterValue } from '~/components/routes/development/real-estate/store-project/statuses-filter-chip'
import {
  REAL_ESTATE_PAGE_READ,
  REAL_ESTATE_TAB_ROUTES,
  REAL_ESTATE_TABS,
} from '~/components/routes/development/real-estate/tabs'
import { withRoutePermissions } from '~/utils/rbac'
import { useAuthorizedTabs } from '~/utils/rbacTabs'

export const Route = createFileRoute('/_authenticated/development/real-estate')({
  staticData: { permission: REAL_ESTATE_PAGE_READ },
  component: RealEstateLayout,
})
withRoutePermissions(Route)

/**
 * Porte la barre d'onglets et les filtres projets partagés entre Carte et Kanban. Les actions
 * propres à un onglet (recherche, création…) sont rendues par l'onglet lui-même, sous les onglets.
 */
function RealEstateLayout() {
  const navigate = useNavigate()
  const authorizedTabs = useAuthorizedTabs(REAL_ESTATE_TABS)

  // Onglet courant lu dans les routes matchées. `useMatches` est indispensable ici : ce layout
  // reste monté d'un onglet à l'autre (seul l'`Outlet` change), et c'est le seul abonnement qui le
  // re-rend à chaque navigation. La route de l'onglet apparaissant aussi dans les matches de ses
  // enfants, l'onglet Kanban reste actif sur `/kanban/$cardId/general`.
  const tab = useMatches({
    select: (matches) =>
      REAL_ESTATE_TABS.find((item) =>
        matches.some((match) => match.fullPath === REAL_ESTATE_TAB_ROUTES[item.value]),
      )?.value,
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatuses, setSelectedStatuses] = useState<StatusFilterValue[]>([])

  return (
    <>
      <AppHeader className="mb-4">
        <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
          Développement foncier
        </Title>
      </AppHeader>

      <AppContainer>
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <AppTabs
            tabs={authorizedTabs.map((item) => ({
              label: item.label,
              value: item.value,
              disabled: !item.authorized,
              tooltip: item.tooltip,
            }))}
            value={tab}
            onTabChange={(value) => {
              if (!authorizedTabs.find((item) => item.value === value)?.authorized) return
              navigate({ to: REAL_ESTATE_TAB_ROUTES[value], search: (prev) => ({ ...prev }) })
            }}
          />
        </div>

        <RealEstateProjectFiltersProvider
          value={{ searchQuery, setSearchQuery, selectedStatuses, setSelectedStatuses }}
        >
          <Outlet />
        </RealEstateProjectFiltersProvider>
      </AppContainer>
    </>
  )
}
